import React, { useState, useEffect, useContext, useRef } from 'react';
import apiClient from '../../apiClient';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import './JournalTemplateForm.css';

function JournalTemplateForm() {
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [isPmoTemplate, setIsPmoTemplate] = useState(false);
  const [templatePrompt, setTemplatePrompt] = useState('');

  const navigate = useNavigate();
  const { id } = useParams();           // id finns ENDAST vid redigering
  const isEditing = Boolean(id);
  const { user } = useContext(UserContext);
  const sectionRefs = useRef([]);

  /* ---------------- Hämta kliniker ---------------- */
  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data || []))
      .catch(err => console.error('Kunde inte hämta clinics:', err));
  }, []);

  /* --------- Hämta existerande mall vid edit ------- */
  useEffect(() => {
    if (!isEditing) return;

    apiClient.get(`/api/journalmallar/${id}`)
      .then(({ data: t }) => {
        if (!t) {
          alert('Journalmall hittades inte');
          navigate('/journalmallar');
          return;
        }
        setTemplateName(t.template_name);
        setSections(t.sections);
        setSelectedClinicId(t.clinic_id?.toString() || '');
        setIsPmoTemplate(!!t.is_pmo_template);
        setTemplatePrompt(t.template_prompt || '');
      })
      .catch(err => {
        console.error('Error fetching template:', err);
        navigate('/journalmallar');
      });
  }, [id, isEditing, navigate]);

  /* ------- Autosize textareor vid inmatning -------- */
  useEffect(() => {
    sections.forEach((s, i) => {
      const titleTA   = sectionRefs.current[`section-title-${i}`];
      const contentTA = sectionRefs.current[`section-content-${i}`];
      if (titleTA)   { titleTA.style.height   = 'auto'; titleTA.style.height   = `${titleTA.scrollHeight}px`; }
      if (contentTA) { contentTA.style.height = 'auto'; contentTA.style.height = `${contentTA.scrollHeight}px`; }
    });
  }, [sections]);

  /* ----------------- Sektion‑helpers ---------------- */
  const moveSectionUp = (i) => {
    if (i === 0) return;
    const upd = [...sections];
    [upd[i - 1], upd[i]] = [upd[i], upd[i - 1]];
    setSections(upd);
  };
  const moveSectionDown = (i) => {
    if (i === sections.length - 1) return;
    const upd = [...sections];
    [upd[i], upd[i + 1]] = [upd[i + 1], upd[i]];
    setSections(upd);
  };

  /* ----------------- Submit (POST/PUT) -------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!templateName || sections.length === 0) {
      setError('Mallnamn och minst en sektion krävs');
      return;
    }

    const payload = {
      template_name:     templateName,
      template_prompt:   templatePrompt,
      clinic_id:         selectedClinicId,
      is_pmo_template:   isPmoTemplate,
      template_type:     'journal',          // extra fält stör ej backend
      sections: sections.map((s, idx) => ({
        section_title: s.section_title || s.title,
        content:       s.content,
        order:         idx,
        tabs_needed:   s.tabs_needed || 0
      }))
    };

    try {
      setSaving(true);
      if (isEditing) {
        await apiClient.put(`/api/journalmallar/${id}`, payload);
      } else {
        await apiClient.post('/api/journalmallar', payload);
      }
      navigate('/journalmallar');
    } catch (err) {
      console.error(err);
      setError('Kunde inte spara journalmall');
    } finally {
      setSaving(false);
    }
  };

  /* ----------------- CRUD‑hjälpare ------------------ */
  const addSection    = () => setSections([...sections, { section_title: '', content: '', tabs_needed: '' }]);
  const removeSection = (i) => setSections(sections.filter((_, idx) => idx !== i));
  const handleSectionChange = (i, field, val) => {
    setSections(sections.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));

    if (field === 'section_title' || field === 'content') {
      const ta = sectionRefs.current[field === 'section_title' ? `section-title-${i}` : `section-content-${i}`];
      if (ta) { ta.style.height = 'auto'; ta.style.height = `${ta.scrollHeight}px`; }
    }
  };

  if (!isClinicLevelAdmin(user)) return <p>Åtkomst nekad</p>;

  /* --------------------- JSX ----------------------- */
  return (
    <div className="journal-template-form-container">
      <h2>{isEditing ? 'Redigera Journalmall' : 'Skapa Ny Journalmall'}</h2>
      <form onSubmit={handleSubmit}>
        {/* Prompt */}
        <div className="form-group">
          <label>Mallspecifik prompt (extrainstruktioner):</label>
          <textarea
            rows={5}
            value={templatePrompt}
            onChange={(e) => setTemplatePrompt(e.target.value)}
            placeholder="Skriv eventuell extra prompt för denna journalmall..."
          />
        </div>

        {/* Mallnamn */}
        <div className="form-group">
          <label>Mallnamn:</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
            className="template-name-input"
          />
        </div>

        {/* Klinik */}
        <div className="form-group">
          <label>Klinik:</label>
          <select
            value={selectedClinicId}
            onChange={(e) => setSelectedClinicId(e.target.value)}
            required
            className="clinic-select"
          >
            <option value="">-- Välj Klinik --</option>
            {clinics.map(c => (
              <option key={c.clinic_id} value={c.clinic_id}>{c.clinic_name}</option>
            ))}
          </select>
        </div>

        {/* PMO‑flagga */}
        <div className="form-group">
          <label>Är detta en PMO-mall?</label>
          <input
            type="checkbox"
            checked={isPmoTemplate}
            onChange={(e) => setIsPmoTemplate(e.target.checked)}
          />
        </div>

        {/* Sektioner */}
        <h3>Sektioner</h3>
        {sections.map((s, i) => (
          <div key={i} className="section-form-group">
            <div className="section-row">
              <div className="section-title-col">
                <label>Rubrik:</label>
                <textarea
                  id={`section-title-${i}`}
                  ref={el => (sectionRefs.current[`section-title-${i}`] = el)}
                  value={s.section_title}
                  onChange={(e) => handleSectionChange(i, 'section_title', e.target.value)}
                  required
                  className="section-title-textarea"
                  rows={1}
                />
              </div>
              <div className="section-content-col">
                <label>Innehåll:</label>
                <textarea
                  id={`section-content-${i}`}
                  ref={el => (sectionRefs.current[`section-content-${i}`] = el)}
                  value={s.content}
                  onChange={(e) => handleSectionChange(i, 'content', e.target.value)}
                  required
                  className="section-content-textarea"
                  rows={1}
                />
                <div className="section-controls">
                  <div className="tabs-control">
                    <label>Tabs:</label>
                    <input
                      type="number"
                      min="0"
                      value={s.tabs_needed || ''}
                      onChange={(e) => handleSectionChange(i, 'tabs_needed', e.target.value)}
                      className="tabs-needed-input"
                    />
                  </div>
                  <div className="move-buttons">
                    <button type="button" onClick={() => moveSectionUp(i)} disabled={i === 0}>↑</button>
                    <button type="button" onClick={() => moveSectionDown(i)} disabled={i === sections.length - 1}>↓</button>
                  </div>
                  <button type="button" onClick={() => removeSection(i)} className="remove-section-button">
                    Ta Bort
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button type="button" onClick={addSection} className="add-section-button">
          Lägg Till Sektion
        </button>

        {/* Actions */}
        <div className="form-actions">
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Sparar…' : 'Spara'}
          </button>
          <button type="button" onClick={() => navigate('/journalmallar')} className="cancel-button">
            Avbryt
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </form>
    </div>
  );
}

export default JournalTemplateForm;
